// @ts-nocheck - untyped
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import DownloadIcon from '@mui/icons-material/Download';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { format, addMinutes } from 'date-fns';
import WebScraperService from '../../../Services/WebScraperService';
import Notification from '../../Layout/Notification';
import { CountryFlag, formatCountryLabel } from './CountryHelpers';
import CreateIncidentDialog from './CreateIncidentDialog';
import IncidentStatusChip from './IncidentStatusChip';
import StatusChipWithInfo from './StatusChipWithInfo';
import '../../../App.scss';
import './WebScraper.scss';
import { notify } from '../../../lib/notify';
import { primitives } from '../../../theme';
import { useAuth } from '../../../Context/AuthenticationContext';
import { hasAccess } from '../../../Utils/roles';
import { WEB_SCRAPER_POSTPAYMENT_ROLE } from '../../../config';

const POLL_MS = 5000;

const WebScrapperOverview = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const canUseServiceNow = hasAccess(user?.roles, [WEB_SCRAPER_POSTPAYMENT_ROLE]);
  const ACCENT = theme.tokens.accent;
  const ACCENT_LIGHT = alpha(ACCENT, 0.08);
  const ACCENT_BORDER = alpha(ACCENT, 0.18);
  const [scrapeResult, setScrapeResult] = useState({ data: [], totalSize: 0, totalPages: 0 });
  const [page, setPage] = useState(0);
  const pageRef = useRef(0);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rppRef = useRef(10);
  useEffect(() => {
    rppRef.current = rowsPerPage;
  }, [rowsPerPage]);

  const [initialLoading, setInitialLoading] = useState(true);
  const initialLoadingRef = useRef(true);
  useEffect(() => {
    initialLoadingRef.current = initialLoading;
  }, [initialLoading]);

  const [busyRowId, setBusyRowId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const autoRefreshRef = useRef(true);
  useEffect(() => {
    autoRefreshRef.current = autoRefresh;
  }, [autoRefresh]);

  const [statusFilter, setStatusFilter] = useState('All');
  const [confirm, setConfirm] = useState({ open: false, jobId: null });

  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef('');
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [selectedJobForIncident, setSelectedJobForIncident] = useState(null);

  const [statusCountsAll, setStatusCountsAll] = useState({
    All: 0,
    'In progress': 0,
    Done: 0,
    Failed: 0,
    Canceled: 0,
  });
  const statusCountsAllRef = useRef({ All: 0, 'In progress': 0, Done: 0, Failed: 0, Canceled: 0 });
  useEffect(() => {
    statusCountsAllRef.current = statusCountsAll;
  }, [statusCountsAll]);
  const [countsLoading, setCountsLoading] = useState(false);

  const [orderBy, setOrderBy] = useState('cratedDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const orderByRef = useRef('cratedDate');
  const orderRef = useRef('desc');
  useEffect(() => {
    orderByRef.current = orderBy;
  }, [orderBy]);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const pollingRef = useRef(null);
  const filterRef = useRef(statusFilter);
  const loadTokenRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    filterRef.current = statusFilter;
  }, [statusFilter]);

  const formatUserName = value => {
    if (!value || !String(value).includes('@')) return 'Unknown user';
    const namePart = value.substring(0, value.lastIndexOf('@'));
    if (!namePart.includes('.')) return namePart;
    return namePart
      .split('.')
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  };

  const fetchPageByFilter = useCallback(
    async (filter, pageNo, pageSize, { silent = false } = {}) => {
      const token = Symbol('load');
      loadTokenRef.current = token;
      if (!silent && initialLoadingRef.current !== false) setInitialLoading(true);
      try {
        let resp;
        const sortBy = orderByRef.current;
        const sortOrder = orderRef.current;
        const search = searchTermRef.current;
        switch (filter) {
          case 'In progress':
            resp = await WebScraperService.getAllInProgress(
              pageNo,
              pageSize,
              sortBy,
              sortOrder,
              search
            );
            break;
          case 'Done':
            resp = await WebScraperService.getAllDone(pageNo, pageSize, sortBy, sortOrder, search);
            break;
          case 'Failed':
            resp = await WebScraperService.getAllFailed(
              pageNo,
              pageSize,
              sortBy,
              sortOrder,
              search
            );
            break;
          case 'Canceled':
            resp = await WebScraperService.getAllCanceled(
              pageNo,
              pageSize,
              sortBy,
              sortOrder,
              search
            );
            break;
          case 'All':
          default:
            resp = await WebScraperService.getScrapingResults(
              pageNo,
              pageSize,
              sortBy,
              sortOrder,
              search
            );
            break;
        }
        if (loadTokenRef.current !== token) return null;
        const { result, totalSize, totalPages } = resp?.data || {};
        const rows = Array.isArray(result) ? result : [];
        setScrapeResult({ data: rows, totalSize: totalSize || 0, totalPages: totalPages || 0 });
        const lastPage = Math.max(
          0,
          Math.ceil((totalSize || 0) / (pageSize || rppRef.current)) - 1
        );
        if (pageRef.current > lastPage) {
          setPage(lastPage);
          pageRef.current = lastPage;
        }
        return rows;
      } catch (err) {
        if (loadTokenRef.current === token)
          Notification.showError(`Unexpected error occurred. (${err.message})`);
        return null;
      } finally {
        if (!silent && mountedRef.current) setInitialLoading(false);
      }
    },
    []
  );

  const fetchStatusTotals = useCallback(async () => {
    setCountsLoading(true);
    try {
      const { data } = await WebScraperService.getScrapingStatusTotals(searchTermRef.current);
      const totals = data?.totals || data || {};
      setStatusCountsAll({
        All: (typeof data?.totalSize === 'number' && data.totalSize) || totals.all || 0,
        'In progress': totals['In progress'] ?? totals.inProgress ?? 0,
        Done: totals.Done ?? totals.done ?? 0,
        Failed: totals.Failed ?? totals.failed ?? 0,
        Canceled: totals.Canceled ?? totals.canceled ?? 0,
      });
    } catch {
      /* ignore */
    } finally {
      setCountsLoading(false);
    }
  }, []);

  const ensurePolling = useCallback(() => {
    const hasActive = statusCountsAllRef.current['In progress'] > 0;
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (!autoRefreshRef.current || !hasActive) return;
    pollingRef.current = window.setInterval(async () => {
      await fetchPageByFilter(filterRef.current, pageRef.current + 1, rppRef.current, {
        silent: true,
      });
      await fetchStatusTotals();
    }, POLL_MS);
  }, [fetchPageByFilter, fetchStatusTotals]);

  useEffect(() => {
    if (!initialLoadingRef.current) {
      fetchPageByFilter(filterRef.current, pageRef.current + 1, rppRef.current, { silent: true });
    }
  }, [orderBy, order, fetchPageByFilter]);

  const refreshAll = useCallback(async () => {
    await fetchPageByFilter(filterRef.current, pageRef.current + 1, rppRef.current, {
      silent: true,
    });
    await fetchStatusTotals();
    ensurePolling();
  }, [fetchPageByFilter, fetchStatusTotals, ensurePolling]);

  const handleDownload = async (id, fileName) => {
    try {
      setBusyRowId(id);
      await WebScraperService.downloadResult(id, fileName);
      Notification.showSuccess(`Result for ${fileName} is ready.`);
    } catch (err) {
      Notification.showError(`Unexpected error occurred. (${err.message})`);
    } finally {
      setBusyRowId(null);
    }
  };

  const viewFile = async (id, fileName) => {
    try {
      setBusyRowId(id);
      await WebScraperService.viewFile(id, fileName);
      Notification.showSuccess(`File ${fileName} is ready.`);
    } catch (err) {
      Notification.showError(`Unexpected error occurred. (${err.message})`);
    } finally {
      setBusyRowId(null);
    }
  };

  const confirmCancel = jobId => setConfirm({ open: true, jobId });
  const closeConfirm = () => setConfirm({ open: false, jobId: null });

  const performCancel = async jobId => {
    if (!jobId) return;
    try {
      setBusyRowId(jobId);
      await WebScraperService.stopJob(jobId);
      Notification.showSuccess('Job canceled.');
      await refreshAll();
    } catch (err) {
      Notification.showError(`Cancel failed. (${err.message})`);
    } finally {
      setBusyRowId(null);
      closeConfirm();
    }
  };

  const handleRetry = async jobId => {
    try {
      if (!WebScraperService.retryJob) {
        Notification.showError('Retry is not supported by backend.');
        return;
      }
      setBusyRowId(jobId);
      await WebScraperService.retryJob(jobId);
      Notification.showSuccess('Job restarted.');
      await refreshAll();
    } catch (err) {
      Notification.showError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Unknown error occurred'
      );
    } finally {
      setBusyRowId(null);
    }
  };

  const handleCreateIncident = job => {
    setSelectedJobForIncident(job);
    setIncidentDialogOpen(true);
  };
  const handleIncidentCreated = async () => {
    await refreshAll();
  };
  const handleCloseIncidentDialog = () => {
    setIncidentDialogOpen(false);
    setSelectedJobForIncident(null);
  };

  const handleViewIncident = incident => {
    if (!incident) return;
    if (incident.incidentUrl) {
      window.open(incident.incidentUrl, '_blank', 'noopener,noreferrer');
    } else if (incident.incidentSysId) {
      const serviceNowUrl = import.meta.env.VITE_SERVICENOW_INSTANCE_URL;
      if (serviceNowUrl)
        window.open(
          `${serviceNowUrl}/nav_to.do?uri=incident.do?sys_id=${incident.incidentSysId}`,
          '_blank',
          'noopener,noreferrer'
        );
      else notify.error('ServiceNow URL not configured');
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await fetchPageByFilter('All', 1, 10, { silent: false });
      await fetchStatusTotals();
      if (alive) ensurePolling();
      setInitialLoading(false);
    })();
    return () => {
      alive = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fetchPageByFilter, fetchStatusTotals, ensurePolling]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      } else if (!document.hidden) {
        fetchPageByFilter(filterRef.current, pageRef.current + 1, rppRef.current, {
          silent: true,
        }).then(() => ensurePolling());
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchPageByFilter, ensurePolling]);

  useEffect(() => {
    setPage(0);
    fetchPageByFilter(statusFilter, 1, rppRef.current, { silent: true }).then(() =>
      ensurePolling()
    );
  }, [statusFilter, fetchPageByFilter, ensurePolling]);

  const isFirstSearchRun = useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(0);
      pageRef.current = 0;
      fetchPageByFilter(filterRef.current, 1, rppRef.current, { silent: true }).then(() =>
        ensurePolling()
      );
      fetchStatusTotals();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchPageByFilter, fetchStatusTotals, ensurePolling]);

  useEffect(() => {
    if (!autoRefresh && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    } else if (autoRefresh) ensurePolling();
  }, [autoRefresh, statusCountsAll, ensurePolling]);

  const chips = [
    { id: 'All', label: 'All', count: statusCountsAll.All },
    { id: 'In progress', label: 'In progress', count: statusCountsAll['In progress'] },
    { id: 'Done', label: 'Done', count: statusCountsAll.Done },
    { id: 'Failed', label: 'Failed', count: statusCountsAll.Failed },
    { id: 'Canceled', label: 'Canceled', count: statusCountsAll.Canceled },
  ];
  const chipBaseSx = {
    height: 24,
    fontSize: 12,
    borderRadius: '6px',
    '& .MuiChip-label': { px: '10px' },
  };
  const activeChipSx = {
    bgcolor: ACCENT,
    color: theme.tokens.fgOnAccent,
    borderColor: ACCENT,
    '&:hover': { bgcolor: ACCENT },
  };
  const inactiveChipSx = {
    borderColor: ACCENT_BORDER,
    color: 'text.primary',
    bgcolor: 'transparent',
  };

  const allColumns = useMemo<MRT_ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        accessorKey: 'country',
        header: 'Country',
        size: 120,
        minSize: 100,
        Cell: ({ row }) => {
          const label = formatCountryLabel(row.original.country);
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <CountryFlag code={row.original.country} title={label} size={12} />
              <span>{label}</span>
            </Box>
          );
        },
      },
      {
        accessorKey: 'fileName',
        header: 'File',
        size: 220,
        minSize: 160,
        Cell: ({ row }) => {
          const v = row.original.fileName;
          const disabled = busyRowId === row.original._id;
          const truncated = v && v.length > 33 ? `${v.substring(0, 33)}…` : v;
          return (
            <Tooltip title={v || ''} arrow placement="top">
              <span
                onClick={() => !disabled && viewFile(row.original._id, v)}
                style={{ cursor: disabled ? 'default' : 'pointer', color: ACCENT, fontWeight: 500 }}
              >
                {truncated}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 150,
        minSize: 140,
        enableSorting: true,
        Cell: ({ row }) => (
          <StatusChipWithInfo
            status={row.original.status}
            jobId={row.original._id}
            errorMessage={row.original.errorMessage}
            canceledBy={row.original.canceledBy}
            cratedDate={row.original.cratedDate}
            completedAt={row.original.completedAt}
            country={row.original.country}
            user={row.original.user}
            fileName={row.original.fileName}
            successRows={row.original.successRows}
            failedRows={row.original.failedRows}
            processedRows={row.original.processedRows}
            totalRows={row.original.totalRows}
            partial={row.original.partial}
            stopReason={row.original.stopReason}
          />
        ),
      },
      {
        accessorKey: 'successRows',
        header: 'Succeeded',
        size: 95,
        minSize: 80,
        Cell: ({ row }) => {
          const showDash = row.original.status !== 'Done' && row.original.status !== 'Failed';
          return (
            <span style={{ color: showDash ? 'var(--t-fg3)' : 'inherit' }}>
              {showDash ? '' : (row.original.successRows ?? 0)}
            </span>
          );
        },
      },
      {
        accessorKey: 'failedRows',
        header: 'Failed',
        size: 85,
        minSize: 75,
        Cell: ({ row }) => {
          const showDash = row.original.status !== 'Done' && row.original.status !== 'Failed';
          const val = row.original.failedRows ?? 0;
          return (
            <span
              style={{
                color: showDash
                  ? 'var(--t-fg3)'
                  : val > 0
                    ? primitives.webScraper.failedRows
                    : 'var(--t-fg3)',
              }}
            >
              {showDash ? '' : val}
            </span>
          );
        },
      },
      {
        accessorKey: 'user',
        header: 'User',
        size: 140,
        minSize: 110,
        Cell: ({ row }) => <span>{formatUserName(row.original.user)}</span>,
      },
      {
        accessorKey: 'cratedDate',
        header: 'Submitted At',
        size: 150,
        minSize: 140,
        Cell: ({ row }) => {
          const v = row.original.cratedDate;
          if (!v) return '';
          const d = new Date(v);
          const utcPlus1 = addMinutes(d, d.getTimezoneOffset() + 60);
          return <span>{format(utcPlus1, 'd MMM yyyy HH:mm')}</span>;
        },
      },
      {
        id: 'serviceNow',
        header: 'ServiceNow',
        size: 140,
        minSize: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const r = row.original;
          const disabled = busyRowId === r._id;

          if (r.serviceNowIncident?.incidentNumber) {
            return (
              <IncidentStatusChip
                incident={r.serviceNowIncident}
                onClick={() => handleViewIncident(r.serviceNowIncident)}
              />
            );
          }
          if (r.status === 'Done') {
            const totalRows = (r.successRows || 0) + (r.failedRows || 0);
            const successRate = totalRows > 0 ? ((r.successRows || 0) / totalRows) * 100 : 0;
            const isBlocked = successRate < 40;
            const isWarning = successRate >= 40 && successRate < 60;
            const btnDisabled = disabled || isBlocked;

            // ServiceNow incident colors are intentionally not themed
            let tooltipText = 'Create ServiceNow Incident';
            let buttonColor = primitives.webScraper.incidentAccent;
            if (isBlocked) {
              tooltipText = `Success rate too low (${successRate.toFixed(1)}%). Minimum 40% required.`;
              buttonColor = primitives.webScraper.incidentBlocked;
            } else if (isWarning) {
              tooltipText = `Warning: Low success rate (${successRate.toFixed(1)}%).`;
              buttonColor = primitives.webScraper.incidentLowSuccess;
            }

            return (
              <Tooltip title={tooltipText} arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleCreateIncident(r)}
                    disabled={btnDisabled}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      py: '2px',
                      px: 1,
                      borderColor: buttonColor,
                      color: buttonColor,
                      minWidth: 0,
                      '&:hover': { borderColor: buttonColor, bgcolor: alpha(buttonColor, 0.08) },
                      '&.Mui-disabled': {
                        borderColor: buttonColor,
                        color: buttonColor,
                        opacity: isBlocked ? 0.5 : 0.38,
                      },
                    }}
                  >
                    Create Incident
                  </Button>
                </span>
              </Tooltip>
            );
          }
          return null;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 90,
        minSize: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          const r = row.original;
          const disabled = busyRowId === r._id;
          if (r.status === 'In progress') {
            return (
              <Tooltip title="Cancel">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => confirmCancel(r._id)}
                    disabled={disabled}
                    color="error"
                  >
                    <StopIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          }
          if (r.status === 'Done') {
            return (
              <Tooltip title="Download">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(r._id, r.fileName)}
                    disabled={disabled}
                    sx={{ color: 'green' }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          }
          if (r.status === 'Canceled' || r.status === 'Failed') {
            // a stopped or failed job still saves everything it scraped, so the report
            // is offered whenever any row was processed - not only for partial ones
            const hasReport = (Number(r.successRows) || 0) + (Number(r.failedRows) || 0) > 0;
            const downloadLabel = r.partial ? 'Download partial report' : 'Download report';
            return (
              <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                {hasReport && (
                  <Tooltip title={downloadLabel}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={downloadLabel}
                        onClick={() => handleDownload(r._id, r.fileName)}
                        disabled={disabled}
                        sx={{ color: 'green' }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Try again">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Try again"
                      onClick={() => handleRetry(r._id)}
                      disabled={disabled}
                      sx={{ color: ACCENT }}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          }
          return null;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyRowId, ACCENT]
  );

  // The ServiceNow column is limited to the Post-payment team.
  const columns = useMemo(
    () => allColumns.filter(col => col.id !== 'serviceNow' || canUseServiceNow),
    [allColumns, canUseServiceNow]
  );

  const sorting = useMemo(() => [{ id: orderBy, desc: order === 'desc' }], [orderBy, order]);

  const wsTable = useMaterialReactTable({
    data: scrapeResult.data,
    columns,
    manualSorting: true,
    enableSortingRemoval: false,
    enableMultiSort: false,
    manualPagination: true,
    rowCount: scrapeResult.totalSize,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableFilters: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    initialState: { density: 'compact' },
    state: {
      sorting,
      pagination: { pageIndex: page, pageSize: rowsPerPage },
      isLoading: initialLoading,
    },
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (next[0]) {
        setOrderBy(next[0].id);
        setOrder(next[0].desc ? 'desc' : 'asc');
      }
    },
    onPaginationChange: updater => {
      const prev = { pageIndex: page, pageSize: rowsPerPage };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const newPage = next.pageIndex;
      const newSize = next.pageSize;
      if (newSize !== rowsPerPage) {
        setRowsPerPage(newSize);
        setPage(0);
        fetchPageByFilter(filterRef.current, 1, newSize, { silent: true }).then(() =>
          ensurePolling()
        );
      } else if (newPage !== page) {
        setPage(newPage);
        fetchPageByFilter(filterRef.current, newPage + 1, rowsPerPage, { silent: true }).then(() =>
          ensurePolling()
        );
      }
    },
    paginationDisplayMode: 'pages',
    enableStickyHeader: true,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        boxShadow: 'none',
        borderRadius: 0,
        border: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableContainerProps: {
      sx: { flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto' },
    },
    muiTableHeadCellProps: {
      sx: { fontSize: 12.5, fontWeight: 600 },
    },
    muiBottomToolbarProps: {
      sx: { minHeight: 44, '& .MuiToolbar-root': { minHeight: 44 } },
    },
    muiTablePaginationProps: {
      sx: {
        minHeight: 44,
        '& .MuiTablePagination-toolbar': { minHeight: 44, py: 0 },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          fontSize: 12.5,
          m: 0,
        },
        '& .MuiTablePagination-select': { fontSize: 12.5 },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: 13,
        py: '6px',
        px: '12px',
        borderBottom: `1px solid ${theme.tokens.tableRowBorder}`,
      },
    },
  });

  return (
    <div
      id="web-scraper-overview-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        height: '100%',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
        <Paper
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: ACCENT_LIGHT,
              border: `1px solid ${ACCENT_BORDER}`,
              borderRadius: 1,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              rowGap: 1,
              m: 1,
              minHeight: 40,
              py: '5px',
              px: 2,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              {chips.map(ch => {
                const active = statusFilter === ch.id;
                const disabled = ch.count === 0 && !active;
                return (
                  <Chip
                    key={ch.id}
                    label={`${ch.label}: ${ch.count}`}
                    size="small"
                    variant={active ? 'filled' : 'outlined'}
                    onClick={disabled ? undefined : () => setStatusFilter(ch.id)}
                    sx={{
                      ...chipBaseSx,
                      ...(active ? activeChipSx : inactiveChipSx),
                      ...(disabled ? { opacity: 0.5, pointerEvents: 'none' } : null),
                    }}
                  />
                );
              })}
              {countsLoading && <CircularProgress size={16} thickness={5} sx={{ ml: 1 }} />}
            </Stack>

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 1, minWidth: 0 }}
            >
              <TextField
                size="small"
                placeholder="Search by country, user or file…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                id="webscraper-search"
                aria-label="Search Web Scraper records"
                InputProps={{
                  style: { fontSize: 13 },
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 0.5 }}>
                      <SearchIcon sx={{ color: ACCENT, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                        aria-label="Clear search"
                        sx={{ color: 'text.secondary' }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: { xs: '100%', sm: 260, md: 320 },
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    height: 30,
                    backgroundColor: 'var(--t-surface)',
                    borderRadius: 999,
                    paddingLeft: 0.5,
                    transition: 'box-shadow 120ms ease, border-color 120ms ease',
                    '& fieldset': { borderColor: ACCENT_BORDER },
                    '&:hover fieldset': { borderColor: alpha(ACCENT, 0.5) },
                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}` },
                    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1 },
                  },
                  '& input::placeholder': { fontStyle: 'italic', opacity: 0.7 },
                }}
              />
              <FormControlLabel
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(_, v) => setAutoRefresh(v)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: ACCENT,
                      },
                    }}
                  />
                }
                label="Auto refresh"
              />
              <Tooltip title="Refresh">
                <span>
                  <IconButton size="small" onClick={refreshAll} sx={{ color: ACCENT }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          <MaterialReactTable table={wsTable} />

          <Dialog open={confirm.open} onClose={closeConfirm}>
            <DialogTitle>Cancel job?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will stop the running job and mark it as <b>Canceled</b>.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeConfirm}>No</Button>
              <Button color="error" onClick={() => performCancel(confirm.jobId)} autoFocus>
                Yes, cancel
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </div>

      {canUseServiceNow && (
        <CreateIncidentDialog
          open={incidentDialogOpen}
          onClose={handleCloseIncidentDialog}
          job={selectedJobForIncident}
          onIncidentCreated={handleIncidentCreated}
        />
      )}
    </div>
  );
};

export default WebScrapperOverview;
